/**
 * FilterDropdown Component
 * A reusable filter dropdown for table columns with sort options and value filters
 */
import { useState, useRef, useEffect } from 'react';
import {
    Button,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    VStack,
    HStack,
    Text,
    Icon,
    Input,
    Divider,
} from '@chakra-ui/react';
import { TbFilter, TbSortAscending, TbSortDescending } from 'react-icons/tb';
import {
    CheckboxMenuSearch,
    CheckboxMenuRow,
    MenuRow,
    MenuSectionLabel,
    CheckboxMenuList,
    checkboxMenuPopoverContentSx,
    checkboxMenuColors,
} from '../../../ui/primitives/CheckboxMenu';

export type SortDirection = 'asc' | 'desc' | null;

export interface FilterOption {
    value: string;
    label: string;
}

export interface NumericFilter {
    min?: number;
    max?: number;
}

interface FilterDropdownProps {
    // Sort configuration
    sortDirection?: SortDirection;
    onSortChange?: (direction: SortDirection) => void;
    
    // Numeric filter (for numbers/currency/percentages)
    numericFilter?: NumericFilter;
    onNumericFilterChange?: (filter: NumericFilter) => void;
    numericType?: 'number' | 'currency' | 'percent' | 'days';
    
    // Categorical filter (for strings like makes, insurances)
    options?: FilterOption[];
    selectedOptions?: string[];
    onOptionsChange?: (selected: string[]) => void;
    
    // Display
    isActive?: boolean;
    size?: 'xs' | 'sm';
}

const FilterDropdown = ({
    sortDirection,
    onSortChange,
    numericFilter,
    onNumericFilterChange,
    numericType = 'number',
    options,
    selectedOptions = [],
    onOptionsChange,
    isActive = false,
    size = 'xs',
}: FilterDropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [localMin, setLocalMin] = useState<string>(numericFilter?.min?.toString() ?? '');
    const [localMax, setLocalMax] = useState<string>(numericFilter?.max?.toString() ?? '');
    const triggerRef = useRef<HTMLButtonElement>(null);

    // Sync local state with props
    useEffect(() => {
        setLocalMin(numericFilter?.min?.toString() ?? '');
        setLocalMax(numericFilter?.max?.toString() ?? '');
    }, [numericFilter]);

    // Reset search when closing
    useEffect(() => {
        if (!isOpen) {
            setSearchTerm('');
        }
    }, [isOpen]);

    const handleSortClick = (direction: SortDirection) => {
        if (onSortChange) {
            // Toggle off if clicking same direction
            onSortChange(sortDirection === direction ? null : direction);
        }
    };

    const handleNumericBlur = () => {
        if (onNumericFilterChange) {
            const min = localMin ? parseFloat(localMin) : undefined;
            const max = localMax ? parseFloat(localMax) : undefined;
            onNumericFilterChange({ min, max });
        }
    };

    const getPlaceholder = (type: 'min' | 'max') => {
        const prefix = type === 'min' ? 'Min' : 'Max';
        switch (numericType) {
            case 'currency':
                return `${prefix} $`;
            case 'percent':
                return `${prefix} %`;
            case 'days':
                return `${prefix} days`;
            default:
                return prefix;
        }
    };

    const filteredOptions = options?.filter(opt =>
        opt.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const hasSort = onSortChange !== undefined;
    const hasNumeric = onNumericFilterChange !== undefined;
    const hasOptions = options && options.length > 0 && onOptionsChange !== undefined;

    // Calculate if filter is active
    const filterIsActive = isActive || 
        sortDirection !== null || 
        (numericFilter?.min !== undefined && numericFilter.min !== null) ||
        (numericFilter?.max !== undefined && numericFilter.max !== null) ||
        (selectedOptions.length > 0 && selectedOptions.length < (options?.length ?? 0));

    return (
        <Popover
            placement="bottom-end"
            isOpen={isOpen}
            onOpen={() => setIsOpen(true)}
            onClose={() => setIsOpen(false)}
            gutter={4}
        >
            <PopoverTrigger>
                <Button
                    ref={triggerRef}
                    variant="ghost"
                    size={size}
                    minW="auto"
                    h={size === 'xs' ? '18px' : '22px'}
                    px={1}
                    color={filterIsActive ? '#0071E3' : 'gray.400'}
                    _hover={{ color: '#0071E3', bg: 'transparent' }}
                    _active={{ bg: 'transparent' }}
                >
                    <Icon as={TbFilter as React.ElementType} boxSize={size === 'xs' ? 3 : 3.5} />
                </Button>
            </PopoverTrigger>
            <PopoverContent w="220px" maxH="350px" overflowY="auto" sx={checkboxMenuPopoverContentSx}>
                <PopoverBody p={0}>
                    <VStack align="stretch" spacing={2} p="8px">
                        {/* Sort Options */}
                        {hasSort && (
                            <>
                                <MenuSectionLabel>Sort</MenuSectionLabel>
                                <VStack align="stretch" spacing={0}>
                                    <MenuRow
                                        kind="single"
                                        isSelected={sortDirection === 'asc'}
                                        icon={<Icon as={TbSortAscending as React.ElementType} boxSize={4} />}
                                        onClick={() => handleSortClick('asc')}
                                    >
                                        Sort Ascending
                                    </MenuRow>
                                    <MenuRow
                                        kind="single"
                                        isSelected={sortDirection === 'desc'}
                                        icon={<Icon as={TbSortDescending as React.ElementType} boxSize={4} />}
                                        onClick={() => handleSortClick('desc')}
                                    >
                                        Sort Descending
                                    </MenuRow>
                                </VStack>
                            </>
                        )}

                        {/* Numeric Range Filter */}
                        {hasNumeric && (
                            <>
                                {hasSort && <Divider />}
                                <MenuSectionLabel>Filter Range</MenuSectionLabel>
                                <HStack spacing={2}>
                                    <Input
                                        size="sm"
                                        type="number"
                                        placeholder={getPlaceholder('min')}
                                        value={localMin}
                                        onChange={(e) => setLocalMin(e.target.value)}
                                        onBlur={handleNumericBlur}
                                        onKeyDown={(e) => e.key === 'Enter' && handleNumericBlur()}
                                        fontSize="xs"
                                    />
                                    <Text fontSize="xs" color="gray.500">to</Text>
                                    <Input
                                        size="sm"
                                        type="number"
                                        placeholder={getPlaceholder('max')}
                                        value={localMax}
                                        onChange={(e) => setLocalMax(e.target.value)}
                                        onBlur={handleNumericBlur}
                                        onKeyDown={(e) => e.key === 'Enter' && handleNumericBlur()}
                                        fontSize="xs"
                                    />
                                </HStack>
                            </>
                        )}

                        {/* Categorical Options Filter */}
                        {hasOptions && (
                            <>
                                {(hasSort || hasNumeric) && <Divider />}
                                <MenuSectionLabel>Filter Values</MenuSectionLabel>
                                <CheckboxMenuSearch
                                    value={searchTerm}
                                    onChange={setSearchTerm}
                                    placeholder="Search..."
                                />
                                <CheckboxMenuList maxH="150px">
                                    {filteredOptions?.map((option) => {
                                        const isSelected = selectedOptions.includes(option.value);
                                        return (
                                            <CheckboxMenuRow
                                                key={option.value}
                                                isSelected={isSelected}
                                                onClick={() => {
                                                    const next = isSelected
                                                        ? selectedOptions.filter(v => v !== option.value)
                                                        : [...selectedOptions, option.value];
                                                    onOptionsChange?.(next);
                                                }}
                                            >
                                                {option.label}
                                            </CheckboxMenuRow>
                                        );
                                    })}
                                    {filteredOptions?.length === 0 && (
                                        <Text fontSize="13px" color={checkboxMenuColors.muted} px="10px" py="8px">
                                            No matches found
                                        </Text>
                                    )}
                                </CheckboxMenuList>
                            </>
                        )}

                        {/* Clear Filters Button */}
                        {filterIsActive && (
                            <>
                                <Divider />
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    colorScheme="red"
                                    onClick={() => {
                                        onSortChange?.(null);
                                        onNumericFilterChange?.({});
                                        if (options) {
                                            onOptionsChange?.(options.map(o => o.value));
                                        }
                                        setLocalMin('');
                                        setLocalMax('');
                                    }}
                                    fontWeight="normal"
                                    h="28px"
                                >
                                    <Text fontSize="xs">Clear Filters</Text>
                                </Button>
                            </>
                        )}
                    </VStack>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
};

export default FilterDropdown;
